import { useTranslation } from "react-i18next";
import { palette } from "../../theme";
import { useIsMobile } from "../useIsMobile";

const fmt = (v: number) => v.toLocaleString("it-IT", { minimumFractionDigits: 1, maximumFractionDigits: 1 });

/** Trend dello score medio (SVG semplice, scala 0-100, soglie rating A/B/C/D). */
export function TrendChart({ data }: { data: { month: string; avg: number | null; count: number }[] }) {
  const { t } = useTranslation();
  const mobile = useIsMobile();
  const pts = data.map((d, i) => ({ ...d, i })).filter((d) => d.avg != null) as { month: string; avg: number; count: number; i: number }[];
  if (!pts.length) return <div style={{ color: palette.muted, padding: 24, textAlign: "center" }}>{t("trendEmpty")}</div>;
  // su smartphone il viewBox è più stretto, così testi e punti restano leggibili senza rimpicciolirsi
  const W = mobile ? 360 : 640, H = mobile ? 220 : 230, L = mobile ? 30 : 36, R = mobile ? 34 : 44, T = 32, B = 30; // T: spazio per l'etichetta del valore sopra il punto (anche a 100)
  const n = data.length;
  const xs = (i: number) => (n === 1 ? (L + W - R) / 2 : L + (i * (W - L - R)) / (n - 1));
  const ys = (v: number) => T + ((100 - v) * (H - T - B)) / 100;
  const line = pts.map((p, k) => `${k ? "L" : "M"}${xs(p.i).toFixed(1)},${ys(p.avg).toFixed(1)}`).join(" ");
  const area = `${line} L${xs(pts[pts.length - 1]!.i).toFixed(1)},${ys(0)} L${xs(pts[0]!.i).toFixed(1)},${ys(0)} Z`;
  const last = pts[pts.length - 1]!;
  const band = (v: number, l: string, c: string) => (
    <text key={l} x={W - 6} y={ys(v) + 4} textAnchor="end" fill={c} fontWeight={700} fontSize={11}>
      {l}
    </text>
  );
  return (
    <svg viewBox={`0 0 ${W} ${H}`} role="img" aria-label={t("ratingTrend")} style={{ width: "100%", height: "auto" }}>
      {[0, 25, 55, 70, 85, 100].map((v) => (
        <g key={v}>
          <line x1={L} x2={W - R} y1={ys(v)} y2={ys(v)} stroke={palette.hair} strokeDasharray={[55, 70, 85].includes(v) ? "3 4" : undefined} />
          <text x={L - 6} y={ys(v) + 4} textAnchor="end" fontSize={11} fill={palette.muted}>
            {v}
          </text>
        </g>
      ))}
      {band(92, "A", palette.ok)}
      {band(77, "B", palette.primary)}
      {band(62, "C", palette.warn)}
      {band(30, "D", palette.bad)}
      <path d={area} fill={palette.primary} fillOpacity={0.1} />
      <path d={line} fill="none" stroke={palette.primary} strokeWidth={2.5} />
      {pts.map((p) => (
        <circle key={p.month} cx={xs(p.i)} cy={ys(p.avg)} r={p === last ? 5 : 3.5} fill={p === last ? palette.primary : palette.card} stroke={palette.primary} strokeWidth={2}>
          <title>{`${p.month}: ${fmt(p.avg)} (${p.count})`}</title>
        </circle>
      ))}
      <text x={xs(last.i) - 8} y={ys(last.avg) - 10} textAnchor="end" fontWeight={800} fontSize={12} fill={palette.heading}>
        {fmt(last.avg)}
      </text>
      {data.map((d, i) =>
        i % (mobile ? 3 : 2) === 0 || i === n - 1 ? (
          <text key={d.month} x={xs(i)} y={H - 8} textAnchor="middle" fontSize={11} fill={palette.muted}>
            {d.month.slice(2).replace("-", "/")}
          </text>
        ) : null,
      )}
    </svg>
  );
}

export function Sparkline({ values }: { values: number[] }) {
  if (values.length < 2) return null;
  const W = 260, H = 50;
  const xs = (i: number) => 6 + (i * (W - 12)) / Math.max(1, values.length - 1);
  const ys = (x: number) => H - 6 - (x / 100) * (H - 12);
  const d = values.map((x, i) => `${i ? "L" : "M"}${xs(i)},${ys(x)}`).join("");
  return (
    <svg viewBox={`0 0 ${W} ${H}`} style={{ maxWidth: 260, width: "100%" }}>
      <path d={d} fill="none" stroke={palette.primary} strokeWidth={2} />
      {values.map((x, i) => (
        <circle key={i} cx={xs(i)} cy={ys(x)} r={i === values.length - 1 ? 4 : 2.5} fill={palette.primary}>
          <title>{fmt(x)}</title>
        </circle>
      ))}
    </svg>
  );
}

/** Barra orizzontale di avanzamento (CAP, copertura). */
export function HBar({ pct, tone }: { pct: number; tone?: "ok" | "info" | "warn" | "bad" }) {
  const c = tone === "ok" ? palette.ok : tone === "warn" ? palette.warn : tone === "bad" ? palette.bad : palette.primary;
  return (
    <div style={{ height: 8, background: palette.sunk, borderRadius: 99, overflow: "hidden", flex: 1, minWidth: 60 }}>
      <i style={{ display: "block", height: "100%", width: `${Math.max(0, Math.min(100, pct))}%`, background: c, borderRadius: 99 }} />
    </div>
  );
}

const ratingColor: Record<string, string> = { A: palette.ok, B: palette.primary, C: palette.warn, D: palette.bad, NQ: palette.heading, "-": palette.secX };

/** Distribuzione dei rating A/B/C/D + non valutati. */
export function RatingDistribution({ dist }: { dist: Record<string, number> }) {
  const keys = ["A", "B", "C", "D", "NQ", "-"];
  const max = Math.max(1, ...keys.map((k) => dist[k] ?? 0));
  return (
    <div style={{ display: "grid", gap: 8 }}>
      {keys.map((k) => (
        <div key={k} style={{ display: "grid", gridTemplateColumns: "26px 1fr 32px", alignItems: "center", gap: 10 }}>
          <span style={{ display: "inline-grid", placeItems: "center", width: 26, height: 26, borderRadius: 7, fontWeight: 800, color: "#fff", background: ratingColor[k] }}>{k === "-" ? "—" : k}</span>
          <div style={{ height: 14, background: palette.sunk, borderRadius: 6, overflow: "hidden" }}>
            <i style={{ display: "block", height: "100%", width: `${((dist[k] ?? 0) / max) * 100}%`, background: ratingColor[k], borderRadius: 6 }} />
          </div>
          <b style={{ textAlign: "right" }}>{dist[k] ?? 0}</b>
        </div>
      ))}
    </div>
  );
}
